import type { FunctionComponent, ReactNode } from 'react';
import { Route } from 'react-router';

import { ThemeProvider } from '@dr.pogodin/react-utils';

import SentryRoutes from 'components/SentryRoutes';

import Error404Page from 'pages/Error404';
import ForbiddenPage from 'pages/Forbidden';
import { UserManagementPage, UsersManagementPage } from 'pages/admin';
import AdminAddNewAdminPage from 'pages/admin/AddNewAdmin';
import AdminAddNewPractitioner from 'pages/admin/AddNewPractitioner';
import AdminAdminProfilePage from 'pages/admin/AdminProfile';
import AdminAdminUsersPage from 'pages/admin/AdminUsers';
import AdminBlogEditorPage from 'pages/admin/BlogEditor';
import AdminBlogItemEditorPage from 'pages/admin/BlogItemEditor';
import AdminContentManagementPage from 'pages/admin/ContentManagement';
import AdminDashboard from 'pages/admin/Dashboard';
import AdminEditVideoPage from 'pages/admin/EditVideo';
import AdminLoginPage from 'pages/admin/Login';
import AdminPasswordResetPage from 'pages/admin/PasswordReset';
import AdminPractitionerApplicationPage from 'pages/admin/PractitionerApplication';
import AdminPractitionerIntroVideo from 'pages/admin/PractitionerIntroVideo';

import AdminPractitionerProfilePage from 'pages/admin/PractitionerProfile';

import AdminPractitionersPage from 'pages/admin/Practitioners';
import AdminProfilePage from 'pages/admin/Profile';
import AdminResearchLib from 'pages/admin/ResearchLib';
import {
  E_ADD_NEW,
  E_ADMIN_USERS,
  E_CONTENT_MANAGEMENT,
  E_ID,
  E_PRACTITIONERS,
  E_PROFILE,
  E_USERS,
  P_ADMIN_BLOG,
  P_ADMIN_PASSWORD_RESET,
  P_ADMIN_PROFILE_INTRO_VIDEO,
  P_ADMIN_RESEARCH_LIB,
  useGlobalState,
} from 'utils';

import defaultThrobberTheme from 'themes/AppThrobber/black.scss';
import primaryButtonTheme from 'themes/Button/admin/primary.scss';
import defaultCheckboxTheme from 'themes/Checkbox/cms/default.scss';
import defaultCustomDropdownTheme from 'themes/CustomDropdown/admin/default.scss';
import defaultDropdownTheme from 'themes/Dropdown/cms/default.scss';
import defaultInputTheme from 'themes/Input/admin/default-50px.scss';
import defaultModalTheme from 'themes/Modal/cms/default.scss';
import defaultTextAreaTheme from 'themes/TextArea/admin/default.scss';
import defaultTooltipTheme from 'themes/Tooltip/admin/default.scss';

const CMS: FunctionComponent = () => {
  const [user] = useGlobalState('user');

  let routes: ReactNode;
  if (user) {
    if (user.permissions.CMS_APP) {
      // TODO: To be pedantic, and perfectionists, we should revise when
      // users get 404 page, and when they get "Forbidden" page (they should
      // see it when accessing any route which exists for some permissions,
      // but not accessible with the user's permissions).
      routes = (
        <>
          <Route element={<AdminDashboard />} path="/" />
          {user.permissions.EDIT_ANY_VIDEO_DETAILS
            ?? user.permissions.VIDEO_UPLOAD ? (
              <Route
                element={<AdminContentManagementPage />}
                path={E_CONTENT_MANAGEMENT}
              />
            ) : null}
          {user.permissions.EDIT_ANY_VIDEO_DETAILS
            ?? user.permissions.VIDEO_UPLOAD ? (
              <Route
                element={<AdminEditVideoPage />}
                path={`${E_CONTENT_MANAGEMENT}/:idOrAddNew`}
              />
            ) : null}
          <Route
            element={<AdminPractitionerIntroVideo />}
            path={P_ADMIN_PROFILE_INTRO_VIDEO}
          />
          <Route
            element={<AdminProfilePage />}
            path={`${E_PROFILE}/*`}
          />
          {user.permissions.BLOG_EDITOR ? (
            <>
              <Route
                element={<AdminBlogEditorPage />}
                path={P_ADMIN_BLOG}
              />
              <Route
                element={<AdminBlogItemEditorPage />}
                path={`${P_ADMIN_BLOG}${E_ID}`}
              />
              <Route
                element={<AdminResearchLib />}
                path={P_ADMIN_RESEARCH_LIB}
              />
            </>
          ) : null}
          {user.permissions.USER_MANAGEMENT ? (
            <>
              <Route
                element={<AdminAdminUsersPage />}
                path={E_ADMIN_USERS}
              />
              <Route
                element={<AdminAddNewAdminPage />}
                path={`${E_ADMIN_USERS}${E_ADD_NEW}`}
              />
              <Route
                element={<AdminPractitionersPage />}
                path={E_PRACTITIONERS}
              />
              <Route
                element={<AdminAddNewPractitioner />}
                path={`${E_PRACTITIONERS}${E_ADD_NEW}`}
              />
              <Route
                element={<AdminAdminProfilePage />}
                path={`${E_ADMIN_USERS}/:id`}
              />
              <Route
                element={<AdminPractitionerProfilePage />}
                path={`${E_PRACTITIONERS}/:id/*`}
              />
              <Route element={<UsersManagementPage />} path={E_USERS} />
              <Route
                element={<UserManagementPage />}
                path={`${E_USERS}/:userId`}
              />
            </>
          ) : null}
          <Route element={<Error404Page />} path="*" />
        </>
      );
    } else if (user.permissions.PRACTITIONER_APPLICATION) {
      routes = <Route element={<AdminPractitionerApplicationPage />} path="*" />;
    } else routes = <Route element={<ForbiddenPage />} path="*" />;
  } else {
    routes = <Route element={<AdminLoginPage />} path="*" />;
  }

  routes = (
    <>
      {routes}
      <Route
        element={<AdminPasswordResetPage />}
        path={P_ADMIN_PASSWORD_RESET}
      />
    </>
  );

  return (
    <ThemeProvider
      themes={{
        AppThrobber: defaultThrobberTheme,
        Button: primaryButtonTheme,
        Checkbox: defaultCheckboxTheme,
        CustomDropdown: defaultCustomDropdownTheme,
        Dropdown: defaultDropdownTheme,
        Input: defaultInputTheme,
        Modal: defaultModalTheme,
        TextArea: defaultTextAreaTheme,
        WithTooltip: defaultTooltipTheme,
      }}
    >
      <SentryRoutes>{routes}</SentryRoutes>
    </ThemeProvider>
  );
};

export default CMS;
